/**
 * Maison Montrose — moteur du catalogue par univers.
 *
 * La page rend TOUS les soins côté serveur, une fois, groupés par univers.
 * Ce module ne fait que masquer, réordonner et compter — aucun aller-retour
 * réseau, donc aucun clignotement entre deux filtres.
 *
 * Les facettes (Besoin / Type de soin / Actif) sont déduites des cartes
 * elles-mêmes : ajouter un produit ou un tag suffit à les mettre à jour,
 * il n'y a rien à configurer dans l'éditeur ni dans Search & Discovery.
 *
 * Le compte affiché sur chaque option est un compte « et si je cochais
 * celle-ci » : on applique les autres groupes, jamais le sien. Une option
 * à zéro est désactivée plutôt que retirée — la liste ne saute pas sous
 * le curseur.
 */

(function () {
  'use strict';

  var SEP = '||';
  var STORAGE_DENSITY = 'mm:catalogue:density';
  var STORAGE_GROUPED = 'mm:catalogue:grouped';

  /** Ordre voulu dans le panneau ; la clé est l'attribut data-* de la carte. */
  var GROUPS = [
    { key: 'besoin', label: 'Besoin', multi: false },
    { key: 'type', label: 'Type de soin', multi: true },
    { key: 'actif', label: 'Actif', multi: true },
  ];

  /* ── Arrivée depuis le méga-menu ────────────────────────────────────────
     Le lien de gamme amène jusqu'à 5 000 px plus bas. On tient à la descente
     — elle raconte où l'on va — mais elle doit être jouée, pas subie : la
     page se pose en haut (voir l'amorce en tête de `mon-collection.liquid`),
     on laisse le temps de lire le haut du catalogue, puis on descend.

     La durée suit la distance, sans jamais dépasser 1,7 s : au-delà on
     n'accompagne plus le visiteur, on le retient. */
  var ARRIVAL_HOLD_MS = 380; // le haut de page reste lisible avant le départ
  var ARRIVAL_BASE_MS = 620;
  var ARRIVAL_PER_PX = 0.2;
  var ARRIVAL_MIN_MS = 760;
  var ARRIVAL_MAX_MS = 1700;
  var ARRIVAL_LANDING = 12; // air laissé entre l'en-tête et la bannière
  var ARRIVAL_SETTLE_MS = 2500; // filet si `load` ou les polices ne viennent jamais
  var ARRIVAL_WARMUP_MS = 900; // au-delà, on part sans attendre les visuels

  /** @param {HTMLElement} root */
  function Catalogue(root) {
    this.root = root;
    this.bar = root.querySelector('[data-mmc-bar]');
    this.panel = root.querySelector('[data-mmc-panel]');
    this.panelGroups = root.querySelector('[data-mmc-panel-groups]');
    this.emptyState = root.querySelector('[data-mmc-empty]');
    this.countBadge = root.querySelector('[data-mmc-filter-count]');
    this.sortSelect = root.querySelector('[data-mmc-sort]');

    this.flat = root.querySelector('[data-mmc-flat]');
    this.flatGrid = root.querySelector('[data-mmc-flat-grid]');
    this.flatMore = this.flat ? this.flat.querySelector('[data-mmc-more]') : null;
    this.switchEl = root.querySelector('[data-mmc-grouped]');

    this.perView = Number(root.dataset.perView) || 8;
    this.selected = {};
    this.pillMatch = [];

    // `groupedPref` est le choix du visiteur ; `grouped` est ce qui s'affiche
    // vraiment — les densités 2 et 6 imposent la grille seule sans écraser
    // la préférence, qui revient dès le retour en 4 par ligne.
    this.groupedPref = !this.switchEl || this.switchEl.classList.contains('is-on');
    this.grouped = this.groupedPref;
    this.flatExpanded = false;
    this.ready = false;

    this.universes = Array.prototype.map.call(
      root.querySelectorAll('[data-mmc-univers]'),
      function (node) {
        var grid = node.querySelector('[data-mmc-grid]');
        var univers = {
          node: node,
          grid: grid,
          more: node.querySelector('[data-mmc-more]'),
          expanded: false,
          cards: Array.prototype.map.call(grid.querySelectorAll('[data-mmc-card]'), readCard),
        };
        univers.cards.forEach(function (card) {
          card.home = grid;
        });
        return univers;
      }
    );

    // `readCard` numérote à l'intérieur de sa grille : sans renumérotation
    // globale, le tri « Notre sélection » de la vue à plat entrelacerait les
    // gammes (tous les premiers soins, puis tous les deuxièmes…).
    var rank = 0;
    this.universes.forEach(function (univers) {
      univers.cards.forEach(function (card) {
        card.order = rank++;
      });
    });

    this.buildFacets();
    this.bindEvents();
    this.restoreGrouped();
    this.restoreDensity();
    this.trackStickyOffset();
    this.readUrl();
    this.ready = true;
    this.apply();
    // L'arrivée est un agrément : un navigateur qui manquerait une API ne
    // doit pas emporter le catalogue avec elle — sans ce filet, une exception
    // ici laisserait la page sans filtres, sans densité et sans repli.
    try {
      this.bindArrivalLinks();
      this.playArrival(false);
    } catch (error) {
      restoreArrivalHash();
    }
  }

  /** @param {HTMLElement} node */
  function readCard(node, index) {
    return {
      node: node,
      order: index,
      besoin: split(node.dataset.besoin),
      type: split(node.dataset.type),
      actif: split(node.dataset.actif),
      price: Number(node.dataset.price) || 0,
      title: node.dataset.title || '',
      date: Number(node.dataset.date) || 0,
    };
  }

  function split(value) {
    if (!value) return [];
    return value
      .split(SEP)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  Catalogue.prototype.eachCard = function (fn) {
    this.universes.forEach(function (univers) {
      univers.cards.forEach(fn);
    });
  };

  /* ─────────────── Facettes ─────────────── */

  Catalogue.prototype.buildFacets = function () {
    if (!this.panelGroups) return;

    var tally = {};
    GROUPS.forEach(function (group) {
      tally[group.key] = new Map();
    });

    this.eachCard(function (card) {
      GROUPS.forEach(function (group) {
        card[group.key].forEach(function (value) {
          tally[group.key].set(value, (tally[group.key].get(value) || 0) + 1);
        });
      });
    });

    var html = '';
    var self = this;

    GROUPS.forEach(function (group, index) {
      var values = Array.from(tally[group.key].entries());
      if (!values.length) return;

      // Les besoins gardent l'ordre alphabétique (liste courte et stable) ;
      // les actifs sont trop nombreux pour ça, on les classe par fréquence.
      if (group.key === 'actif') {
        values.sort(function (a, b) {
          return b[1] - a[1] || a[0].localeCompare(b[0], 'fr');
        });
      } else {
        values.sort(function (a, b) {
          return a[0].localeCompare(b[0], 'fr');
        });
      }

      self.selected[group.key] = [];

      // Un accordéon par famille ; seule la première est ouverte à l'arrivée.
      html += '<details class="mmc-group" data-mmc-group="' + group.key + '"' + (index === 0 ? ' open' : '') + '>';
      html += '<summary class="mmc-group__title">' + escapeHtml(group.label) + '<span class="mmc-group__badge" data-mmc-group-badge></span></summary>';
      html += '<div class="mmc-group__list">';
      values.forEach(function (entry) {
        var value = entry[0];
        html +=
          '<label class="mmc-check" data-mmc-option="' +
          escapeHtml(value) +
          '"><input type="checkbox" value="' +
          escapeHtml(value) +
          '"><span>' +
          escapeHtml(value) +
          '</span><span class="mmc-check__count" data-mmc-option-count>' +
          entry[1] +
          '</span></label>';
      });
      html += '</div></details>';
    });

    this.panelGroups.innerHTML = html;
  };

  /* ─────────────── Évènements ─────────────── */

  Catalogue.prototype.bindEvents = function () {
    var self = this;

    this.root.querySelectorAll('[data-mmc-filter-toggle]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.togglePanel();
      });
    });

    this.root.querySelectorAll('[data-mmc-reset]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.reset();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.panel && !self.panel.hasAttribute('hidden')) {
        self.togglePanel();
      }
    });

    if (this.panelGroups) {
      this.panelGroups.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.type !== 'checkbox') return;
        var group = input.closest('[data-mmc-group]').dataset.mmcGroup;
        var list = self.selected[group];
        var at = list.indexOf(input.value);
        if (input.checked && at === -1) list.push(input.value);
        if (!input.checked && at !== -1) list.splice(at, 1);
        self.apply();
      });
    }

    this.root.querySelectorAll('[data-mmc-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        self.root.querySelectorAll('[data-mmc-pill]').forEach(function (other) {
          other.classList.toggle('is-active', other === pill);
          other.setAttribute('aria-pressed', other === pill ? 'true' : 'false');
        });
        self.pillMatch = split(pill.dataset.match.replace(/\s*,\s*/g, SEP));
        self.collapseAll();
        self.apply();
      });
    });

    if (this.switchEl) {
      this.switchEl.addEventListener('click', function () {
        self.groupedPref = !self.groupedPref;
        self.setGrouped(self.groupedPref, true);
      });
    }

    this.root.querySelectorAll('[data-mmc-density]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.setDensity(button.dataset.mmcDensity, true);
      });
    });

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        self.apply();
      });
    }

    this.universes.forEach(function (univers) {
      if (!univers.more) return;
      univers.more.addEventListener('click', function () {
        univers.expanded = true;
        self.apply();
      });
    });

    if (this.flatMore) {
      this.flatMore.addEventListener('click', function () {
        self.flatExpanded = true;
        self.apply();
      });
    }
  };

  Catalogue.prototype.togglePanel = function () {
    if (!this.panel) return;
    var open = this.panel.hasAttribute('hidden');
    this.panel.toggleAttribute('hidden', !open);
    // Tiroir modal : la page ne défile plus derrière, Échap referme.
    document.body.classList.toggle('mmc-drawer-open', open);
    if (open) {
      var close = this.panel.querySelector('.mmc-panel__close');
      if (close) close.focus();
    }
    this.root.querySelectorAll('[data-mmc-filter-toggle]').forEach(function (button) {
      if (button.hasAttribute('aria-expanded')) {
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });
  };

  Catalogue.prototype.reset = function () {
    var self = this;
    GROUPS.forEach(function (group) {
      self.selected[group.key] = [];
    });
    if (this.panelGroups) {
      this.panelGroups.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
        input.checked = false;
      });
    }
    this.collapseAll();
    this.apply();
  };

  Catalogue.prototype.collapseAll = function () {
    this.flatExpanded = false;
    this.universes.forEach(function (univers) {
      univers.expanded = false;
    });
  };

  /* ─────────────── Groupement par gamme ─────────────── */

  Catalogue.prototype.setGrouped = function (grouped, persist) {
    this.grouped = grouped;
    this.paintSwitch();
    if (persist) {
      try {
        localStorage.setItem(STORAGE_GROUPED, grouped ? '1' : '0');
      } catch (error) {
        /* navigation privée */
      }
    }
    if (this.ready) this.apply();
  };

  Catalogue.prototype.paintSwitch = function () {
    if (!this.switchEl) return;
    this.switchEl.classList.toggle('is-on', this.grouped);
    this.switchEl.setAttribute('aria-pressed', this.grouped ? 'true' : 'false');
  };

  Catalogue.prototype.restoreGrouped = function () {
    var stored = null;
    try {
      stored = localStorage.getItem(STORAGE_GROUPED);
    } catch (error) {
      stored = null;
    }
    if (stored === '0' || stored === '1') {
      this.groupedPref = stored === '1';
      this.grouped = this.groupedPref;
      this.paintSwitch();
    }
  };

  /* ─────────────── Densité ─────────────── */

  Catalogue.prototype.setDensity = function (value, persist) {
    this.root.dataset.density = value;
    this.root.querySelectorAll('[data-mmc-density]').forEach(function (button) {
      var on = button.dataset.mmcDensity === value;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // En 2 par ligne on veut la contemplation, en 6 la vue d'ensemble : dans
    // les deux cas les bannières de gamme coupent la lecture. Elles ne
    // reviennent qu'en 4 par ligne, et seulement si le visiteur les veut.
    var forced = value === '2' || value === '6';
    if (this.switchEl) this.switchEl.disabled = forced;
    this.setGrouped(forced ? false : this.groupedPref, false);

    if (persist) {
      try {
        localStorage.setItem(STORAGE_DENSITY, value);
      } catch (error) {
        /* navigation privée : on garde simplement la densité par défaut */
      }
    }
  };

  Catalogue.prototype.restoreDensity = function () {
    var stored = null;
    try {
      stored = localStorage.getItem(STORAGE_DENSITY);
    } catch (error) {
      stored = null;
    }
    if (stored && this.root.querySelector('[data-mmc-density="' + stored + '"]')) {
      this.setDensity(stored, false);
    }
  };

  /* ─────────────── Barre collante ─────────────── */

  Catalogue.prototype.trackStickyOffset = function () {
    var self = this;
    var header = document.querySelector('.mon-premium-header-section, .header-section, header');

    // Le header maison est `sticky` sur grand écran (il occupe sa place dans
    // le flux) et `absolute` sur mobile (il se pose PAR-DESSUS la page). Les
    // deux cas demandent l'inverse l'un de l'autre : dans le premier la barre
    // de filtres doit se coller sous le header, dans le second elle se colle
    // en haut et c'est le hero qui doit se dégager.
    function measure() {
      // Une mesure prise avant que la mise en page ne soit posée (viewport à
      // zéro) choisirait la mauvaise branche et figerait un hero décalé.
      if (!header || window.innerWidth < 320) return;
      var height = header.getBoundingClientRect().height;
      if (!(height > 0 && height < 260)) return;

      var overlays = getComputedStyle(header).position !== 'sticky';
      self.root.style.setProperty('--mmc-sticky-top', overlays ? '0px' : Math.round(height) + 'px');
      self.root.style.setProperty('--mmc-header-overlay', overlays ? Math.round(height) + 'px' : '0px');
    }

    measure();
    requestAnimationFrame(measure);
    window.addEventListener('load', measure);
    window.addEventListener('resize', debounce(measure, 150));

    if (this.bar && 'IntersectionObserver' in window) {
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;height:1px;width:1px;';
      this.bar.parentNode.insertBefore(sentinel, this.bar);
      new IntersectionObserver(
        function (entries) {
          self.bar.classList.toggle('is-pinned', !entries[0].isIntersecting);
        },
        { rootMargin: '-120px 0px 0px 0px' }
      ).observe(sentinel);
    }
  };

  /* ─────────────── Application des filtres ─────────────── */

  Catalogue.prototype.matches = function (card, skipGroup) {
    if (this.pillMatch.length) {
      var hit = card.type.some(function (value) {
        return this.pillMatch.indexOf(value) !== -1;
      }, this);
      if (!hit) return false;
    }

    for (var i = 0; i < GROUPS.length; i++) {
      var key = GROUPS[i].key;
      if (key === skipGroup) continue;
      var chosen = this.selected[key] || [];
      if (!chosen.length) continue;
      var found = card[key].some(function (value) {
        return chosen.indexOf(value) !== -1;
      });
      if (!found) return false;
    }
    return true;
  };


  /* ─────────────── URL ─────────────── */

  /**
   * Les filtres vivent dans l'URL (?besoin=Regard&type=Sérums). C'est ce qui
   * rend une sélection partageable, ce qui permet au retour arrière de faire
   * ce qu'on attend de lui, et ce qui permet aux suggestions « Par besoin »
   * de la recherche d'atterrir sur un catalogue déjà filtré.
   */
  Catalogue.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var self = this;
    var touched = false;

    GROUPS.forEach(function (group) {
      var raw = params.get(group.key);
      if (!raw) return;
      self.selected[group.key] = raw.split(',').map(function (value) {
        return value.trim();
      }).filter(Boolean);
      touched = true;
    });

    var geste = params.get('geste');
    if (geste) {
      var pill = Array.prototype.find.call(
        this.root.querySelectorAll('[data-mmc-pill]'),
        function (node) {
          return node.textContent.trim().toLowerCase() === geste.trim().toLowerCase();
        }
      );
      if (pill) {
        pill.click();
        touched = true;
      }
    }

    if (!touched || !this.panelGroups) return;

    this.panelGroups.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      var key = input.closest('[data-mmc-group]').dataset.mmcGroup;
      input.checked = (self.selected[key] || []).indexOf(input.value) !== -1;
    });

    // Une sélection venue de l'extérieur mérite d'être visible, sinon le
    // visiteur voit un catalogue amputé sans comprendre pourquoi.
    // Le tiroir est modal : on ne l'ouvre pas de nous-mêmes, le badge du
    // bouton « Filtrer » signale déjà les filtres actifs.
  };

  Catalogue.prototype.writeUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    var self = this;

    GROUPS.forEach(function (group) {
      var chosen = self.selected[group.key] || [];
      if (chosen.length) {
        params.set(group.key, chosen.join(','));
      } else {
        params.delete(group.key);
      }
    });

    var active = this.root.querySelector('[data-mmc-pill].is-active');
    if (active && active.dataset.match) {
      params.set('geste', active.textContent.trim());
    } else {
      params.delete('geste');
    }

    // L'ancre de gamme est reposée par `playArrival` une fois l'arrivée finie :
    // la perdre au premier filtre rendrait le lien du menu impartageable et
    // ferait retomber un rechargement en haut du catalogue.
    var query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? '?' + query : '') + (window.location.hash || '')
    );
  };

  Catalogue.prototype.refreshGroupBadges = function () {
    var self = this;
    if (!this.panelGroups) return;
    this.panelGroups.querySelectorAll('[data-mmc-group]').forEach(function (group) {
      var badge = group.querySelector('[data-mmc-group-badge]');
      var n = (self.selected[group.dataset.mmcGroup] || []).length;
      if (badge) badge.textContent = n ? String(n) : '';
    });
  };

  Catalogue.prototype.apply = function () {
    var sort = this.sortSelect ? this.sortSelect.value : 'editorial';
    this.refreshGroupBadges();
    var total = this.grouped ? this.renderGrouped(sort) : this.renderFlat(sort);

    if (this.emptyState) this.emptyState.hidden = total > 0;

    this.refreshCounts();
    this.refreshBadge();
    this.writeUrl();
  };

  /** Une bannière par gamme, puis sa grille. */
  Catalogue.prototype.renderGrouped = function (sort) {
    var self = this;
    var total = 0;

    if (this.flat) this.flat.hidden = true;

    this.universes.forEach(function (univers) {
      var visible = [];
      var rest = [];

      univers.cards.forEach(function (card) {
        (self.matches(card, null) ? visible : rest).push(card);
      });

      sortCards(visible, sort);

      // Les cartes ont pu partir dans la grille à plat : on les rapatrie
      // toutes, y compris celles que le filtre écarte, sinon elles restent
      // orphelines dans l'autre conteneur.
      visible.concat(rest).forEach(function (card) {
        univers.grid.appendChild(card.node);
      });

      var limit = univers.expanded ? visible.length : Math.min(self.perView, visible.length);

      rest.forEach(function (card) {
        card.node.hidden = true;
      });
      visible.forEach(function (card, index) {
        card.node.hidden = index >= limit;
      });

      univers.node.hidden = visible.length === 0;
      if (univers.more) univers.more.hidden = visible.length <= limit;
      total += visible.length;
    });

    return total;
  };

  /** Une seule grille, tous les soins, aucune bannière. */
  Catalogue.prototype.renderFlat = function (sort) {
    var self = this;
    var visible = [];

    this.universes.forEach(function (univers) {
      univers.node.hidden = true;
      if (univers.more) univers.more.hidden = true;
      univers.cards.forEach(function (card) {
        if (self.matches(card, null)) {
          visible.push(card);
        } else {
          card.node.hidden = true;
        }
      });
    });

    sortCards(visible, sort);
    visible.forEach(function (card) {
      self.flatGrid.appendChild(card.node);
    });

    var limit = this.flatExpanded ? visible.length : Math.min(this.perView * 3, visible.length);
    visible.forEach(function (card, index) {
      card.node.hidden = index >= limit;
    });

    if (this.flat) this.flat.hidden = visible.length === 0;
    if (this.flatMore) this.flatMore.hidden = visible.length <= limit;

    return visible.length;
  };

  function sortCards(cards, mode) {
    var comparators = {
      editorial: function (a, b) {
        return a.order - b.order;
      },
      'price-asc': function (a, b) {
        return a.price - b.price || a.order - b.order;
      },
      'price-desc': function (a, b) {
        return b.price - a.price || a.order - b.order;
      },
      new: function (a, b) {
        return b.date - a.date || a.order - b.order;
      },
      az: function (a, b) {
        return a.title.localeCompare(b.title, 'fr');
      },
    };
    cards.sort(comparators[mode] || comparators.editorial);
  }

  Catalogue.prototype.refreshCounts = function () {
    if (!this.panelGroups) return;
    var self = this;

    GROUPS.forEach(function (group) {
      var container = self.panelGroups.querySelector('[data-mmc-group="' + group.key + '"]');
      if (!container) return;

      var counts = new Map();
      self.eachCard(function (card) {
        if (!self.matches(card, group.key)) return;
        card[group.key].forEach(function (value) {
          counts.set(value, (counts.get(value) || 0) + 1);
        });
      });

      container.querySelectorAll('[data-mmc-option]').forEach(function (label) {
        var value = label.dataset.mmcOption;
        var count = counts.get(value) || 0;
        var input = label.querySelector('input');
        label.querySelector('[data-mmc-option-count]').textContent = count;
        label.classList.toggle('is-checked', input.checked);
        label.classList.toggle('is-disabled', count === 0 && !input.checked);
      });
    });
  };

  Catalogue.prototype.refreshBadge = function () {
    if (!this.countBadge) return;
    var self = this;
    var count = GROUPS.reduce(function (sum, group) {
      return sum + (self.selected[group.key] || []).length;
    }, 0);
    this.countBadge.textContent = count;
    this.countBadge.hidden = count === 0;
  };

  /* ─────────────── Arrivée sur une gamme ─────────────── */

  /**
   * Descend jusqu'à la gamme visée par le méga-menu.
   * @param {boolean} immediate - vrai quand le clic a lieu sur la page même,
   *   auquel cas il n'y a rien à attendre : la mise en page est déjà posée.
   */
  Catalogue.prototype.playArrival = function (immediate) {
    var id = window.__mmcArrival;
    if (!id) {
      restoreArrivalHash();
      return;
    }
    window.__mmcArrival = null;

    var self = this;
    var target = document.getElementById(id);
    if (!target || !this.root.contains(target)) return;

    // Densité 2 ou 6, ou bascule « Par gamme » éteinte : les bannières sont
    // masquées et l'ancre n'a plus de boîte. Le lien du menu promet une
    // gamme — on rétablit la vue groupée pour cette visite seulement, sans
    // écrire dans la préférence enregistrée.
    if (!isVisible(target)) {
      var density = this.root.dataset.density;
      if (density === '2' || density === '6') this.setDensity('4', false);
      this.setGrouped(true, false);
      if (!isVisible(target)) return;
    }

    var scroller = scrollerFor(target);

    function destination() {
      var origin =
        scroller === document.scrollingElement || scroller === document.documentElement || scroller === document.body
          ? 0
          : scroller.getBoundingClientRect().top;
      var landing = ARRIVAL_LANDING;
      var header = document.querySelector('.mon-premium-header-section, .header-section, header');
      if (header) {
        var headerBox = header.getBoundingClientRect();
        if (headerBox.top <= origin + 1 && headerBox.bottom > origin) {
          landing = Math.max(landing, Math.round(headerBox.bottom - origin) + ARRIVAL_LANDING);
        }
      }
      var top = target.getBoundingClientRect().top - origin + scroller.scrollTop - landing;
      var furthest = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      return Math.max(0, Math.min(Math.round(top), furthest));
    }

    function land() {
      // L'ancre revient dans la barre d'adresse une fois posé : le lien
      // redevient partageable, et `writeUrl` la conserve ensuite.
      window.__mmcArrivalHash = null;
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + id);
      }
      // Un lien de navigation doit aussi déplacer le clavier, sinon la
      // tabulation repart du haut du catalogue.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        /* navigateurs sans options de focus : la position visuelle suffit */
      }
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      withoutSmoothScroll(scroller, function () {
        scroller.scrollTop = destination();
      });
      land();
      return;
    }

    function go() {
      warmUp(target, function () {
        self.flyTo(scroller, destination, land);
      });
    }

    if (immediate) {
      go();
      return;
    }

    whenSettled(function () {
      // Le visiteur a repris la main avant le départ : on ne le déplace plus.
      if (scroller.scrollTop > 4) return;
      go();
    });
  };

  /**
   * Le vol proprement dit.
   * @param {Element} scroller
   * @param {function(): number} destination - relue à chaque trame
   * @param {function()} onLand
   */
  Catalogue.prototype.flyTo = function (scroller, destination, onLand) {
    var from = scroller.scrollTop;
    var span = Math.abs(destination() - from);
    if (span < 8) {
      onLand();
      return;
    }

    var duration = Math.max(ARRIVAL_MIN_MS, Math.min(ARRIVAL_MAX_MS, ARRIVAL_BASE_MS + span * ARRIVAL_PER_PX));
    var events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    var frame = 0;
    var start = 0;
    var aborted = false;

    // `scroll-behavior: smooth` du thème s'applique AUSSI aux écritures de
    // `scrollTop` : laissé en place, le navigateur animerait par-dessus notre
    // animation et les deux se battraient, chaque trame repartant d'une
    // position que le compositeur n'a pas fini de rejoindre.
    var restore = suspendSmoothScroll(scroller);

    function stop() {
      aborted = true;
    }

    events.forEach(function (name) {
      window.addEventListener(name, stop, { passive: true });
    });

    function finish(landed) {
      if (frame) window.cancelAnimationFrame(frame);
      events.forEach(function (name) {
        window.removeEventListener(name, stop);
      });
      restore();
      if (landed) onLand();
    }

    function tick(now) {
      if (aborted) {
        finish(false);
        return;
      }
      if (!start) start = now;
      var ratio = Math.min(1, (now - start) / duration);
      // La cible peut encore glisser de quelques pixels pendant le vol — une
      // police de repli remplacée, une bannière décodée. On la relit à chaque
      // trame plutôt que de viser un point figé au départ.
      var goal = destination();
      scroller.scrollTop = from + (goal - from) * arrivalEase(ratio);
      if (ratio < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      finish(true);
    }

    frame = window.requestAnimationFrame(tick);
  };

  /* Déjà sur le catalogue, un clic sur la même entrée de menu est une simple
     navigation de fragment : le navigateur la jouerait avec son propre
     défilement doux, plus court et plus sec. On rejoue la nôtre. */
  Catalogue.prototype.bindArrivalLinks = function () {
    var self = this;

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!link || (link.target && link.target !== '_self')) return;

      var url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (!/^#univers-[a-z0-9-]+$/i.test(url.hash)) return;

      var target = document.getElementById(url.hash.slice(1));
      if (!target || !self.root.contains(target)) return;

      event.preventDefault();
      window.__mmcArrival = url.hash.slice(1);
      self.playArrival(true);
    });
  };

  /* ─────────────── Utilitaires ─────────────── */

  /** Le conteneur qui défile réellement : `.page-wrapper` au-dessus de
      990 px, la page elle-même en dessous. */
  function scrollerFor(node) {
    var el = node.parentNode;
    while (el && el.nodeType === 1) {
      var overflow = getComputedStyle(el).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentNode;
    }
    return document.scrollingElement || document.documentElement;
  }

  function suspendSmoothScroll(scroller) {
    var had = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    return function () {
      scroller.style.scrollBehavior = had;
    };
  }

  function withoutSmoothScroll(scroller, fn) {
    var restore = suspendSmoothScroll(scroller);
    fn();
    restore();
  }

  /** Rechargement d'un lien de gamme : l'amorce a retiré le fragment pour
      éviter un saut par-dessus la position restaurée. On le repose, sinon
      l'URL perdrait son ancre au premier F5. */
  function restoreArrivalHash() {
    var id = window.__mmcArrivalHash;
    if (!id) return;
    window.__mmcArrivalHash = null;
    if (!window.history || !window.history.replaceState) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + id);
  }

  function isVisible(node) {
    return !node.hidden && node.getClientRects().length > 0;
  }

  /** Une courbe de Bézier, comme en CSS : départ posé, longue descente,
      arrivée qui s'éteint — `cubic-bezier(0.45, 0, 0.12, 1)`. Un `ease-out`
      pur partirait à pleine vitesse dès la première trame, ce qui se lit
      comme un saut plutôt que comme un déplacement. */
  function arrivalEase(x) {
    return bezier(x, 0.45, 0, 0.12, 1);
  }

  function bezier(x, x1, y1, x2, y2) {
    function at(a, b, t) {
      var c = 3 * a;
      var b2 = 3 * (b - a) - c;
      var a3 = 1 - c - b2;
      return ((a3 * t + b2) * t + c) * t;
    }
    function slope(a, b, t) {
      var c = 3 * a;
      var b2 = 3 * (b - a) - c;
      var a3 = 1 - c - b2;
      return (3 * a3 * t + 2 * b2) * t + c;
    }

    var t = x;
    for (var i = 0; i < 6; i++) {
      var delta = at(x1, x2, t) - x;
      var d = slope(x1, x2, t);
      if (Math.abs(delta) < 1e-4 || d === 0) break;
      t -= delta / d;
    }
    return at(y1, y2, t);
  }

  /** Un onglet ouvert en arrière-plan — clic milieu, « ouvrir dans un nouvel
      onglet » — met `requestAnimationFrame` en pause : la descente s'y figerait
      à mi-course, pour sauter d'un bloc au moment où le visiteur revient. On
      attend donc que la page soit regardée pour partir. */
  function whenVisible(callback) {
    if (!document.hidden) {
      callback();
      return;
    }
    document.addEventListener('visibilitychange', function onVisible() {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onVisible);
      callback();
    });
  }

  /** On ne part qu'une fois la page vraiment posée : `load` pour les gabarits
      d'images, `fonts.ready` parce qu'un remplacement de police décale les
      bannières de plusieurs dizaines de pixels, puis deux trames pour laisser
      le premier rendu s'afficher. C'est cette attente qui fait la différence
      entre une descente propre et un vol qui saute. */
  function whenSettled(callback) {
    var fired = false;
    var pending = 1;

    function go() {
      if (fired) return;
      fired = true;
      whenVisible(function () {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            window.setTimeout(callback, ARRIVAL_HOLD_MS);
          });
        });
      });
    }

    function step() {
      pending -= 1;
      if (pending <= 0) go();
    }

    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      pending += 1;
      document.fonts.ready.then(step, step);
    }

    if (document.readyState === 'complete') step();
    else window.addEventListener('load', step, { once: true });

    // Filet : une image lente, une police jamais résolue, et l'arrivée
    // n'aurait tout simplement pas lieu.
    window.setTimeout(go, ARRIVAL_SETTLE_MS);
  }

  /** Les visuels de la gamme visée sont en `lazy` et ne se déclencheraient
      qu'à l'atterrissage : on les réclame avant de partir, pour ne pas se
      poser sur des cadres gris. Les cartes masquées sont laissées de côté. */
  function warmUp(node, callback) {
    var images = Array.prototype.filter.call(node.querySelectorAll('img[loading="lazy"]'), function (img) {
      return !img.closest('[hidden]');
    });
    if (!images.length) {
      callback();
      return;
    }

    var left = images.length;
    var settled = false;

    function step() {
      left -= 1;
      if (left > 0 || settled) return;
      settled = true;
      callback();
    }

    images.forEach(function (img) {
      img.loading = 'eager';
      if (img.complete) {
        step();
        return;
      }
      img.addEventListener('load', step, { once: true });
      img.addEventListener('error', step, { once: true });
    });

    window.setTimeout(function () {
      if (settled) return;
      settled = true;
      callback();
    }, ARRIVAL_WARMUP_MS);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character];
    });
  }

  /* Les méthodes vivent sur le prototype : instancier plus haut dans le
     fichier lèverait « buildFacets is not a function ». */
  document.querySelectorAll('[data-mmc]').forEach(function (root) {
    new Catalogue(root);
  });
})();
